import { useState } from "react";
import {
  ArrowLeft, ChevronRight, MapPin, Sparkles, Target, Gamepad2,
  Wind, Timer, Users, Star, Zap, Phone, Mail,
  Menu, X, ExternalLink
} from "lucide-react";

// ── Wildcatz Brand Colors ──
const C = {
  teal: "#1a8a7d",
  tealDark: "#147267",
  tealLight: "#2ab5a5",
  red: "#c41e1e",
  redLight: "#e53535",
  black: "#0c1215",
  darkBg: "#0a0e10",
  card: "#111618",
  cardHover: "#1a2024",
  border: "#1e2a2e",
  borderLight: "#e2e8f0",
  white: "#ffffff",
  offWhite: "#f8fafb",
  lightText: "#1a1a1a",
  lightMuted: "#64748b",
  lightDim: "#94a3b8",
  text: "#e4e4e7",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  purple: "#6C5CE7",
  orange: "#f97316",
  cyan: "#06b6d4",
  yellow: "#eab308",
};

// ── App Store Links ──
const APP_STORE_URL = "https://apps.apple.com/au/app/wildcatz-indoor-sports/id6761478923";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.wisasports.app"; // LIVE 2026-06-11 — Android app launched on Google Play

const COMING_SOON = [
  {
    name: "Tenpin Bowling",
    emoji: "🎳",
    color: C.orange,
    tagline: "Strikes, spares & good times",
    desc: "4 tenpin lanes coming soon to Wildcatz! Perfect for social leagues, birthday parties, corporate events, and casual fun with mates. Modern scoring systems, glow bowling nights, and bumper options for the kids.",
    features: ["4 tenpin lanes", "Social & competitive leagues", "Glow bowling nights", "Birthday party packages", "Bumper bowling for kids"],
    icon: Target,
    image: "https://images.unsplash.com/photo-1545056453-f0359c3df6db?w=800&h=500&fit=crop",
  },
  {
    name: "Golf Simulators",
    emoji: "⛳",
    color: C.teal,
    tagline: "World-class courses, indoor comfort",
    desc: "Play world-famous courses without leaving Townsville. Our high-definition golf simulators bring the best courses to Wildcatz. Perfect for practice, lessons, social rounds, and competitions — rain, hail, or shine.",
    features: ["100+ world-famous courses", "Swing analysis & coaching tools", "Multiplayer competitions", "Corporate golf days", "All-weather play"],
    icon: Wind,
    // 2026-05-27: Unsplash photos for "golf simulator" keep coming back
    // as outdoor courses. Using a CSS gradient + giant ⛳ emoji as the
    // hero instead — guaranteed to not look like outdoor golf. Replace
    // with a real photo of the Wildcatz bay once it's installed by
    // dropping a file at /wildcatz/public/golf-sim.jpeg and setting
    // image: "/golf-sim.jpeg" below.
    image: "gradient:golf",
  },
  {
    name: "AR Sports",
    emoji: "🎯",
    color: C.cyan,
    tagline: "Augmented reality meets real sport",
    desc: "Experience the future of indoor sports with our augmented reality arena. Shooting, basketball, soccer, tennis, boxing, LED grid plus more! Interactive games that blend physical activity with cutting-edge AR technology.",
    features: ["Shooting & basketball", "Soccer & tennis", "Boxing & LED grid", "Team-based competitions", "Suitable for all ages"],
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=500&fit=crop",
  },
  {
    name: "Virtual Reality",
    emoji: "🥽",
    color: C.purple,
    tagline: "Step into another world",
    desc: "Immersive VR gaming stations with multiplayer adventures, simulations, and experiences. From action-packed shooters to relaxing virtual worlds — strap on a headset and leave reality behind.",
    features: ["Multiplayer VR experiences", "Adventure & action games", "VR sports simulations", "Group bookings available", "Suitable for ages 10+"],
    icon: Gamepad2,
    image: "https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?w=800&h=500&fit=crop",
  },
  {
    name: "Arcade",
    emoji: "🕹️",
    color: "#f59e0b",
    tagline: "Classic and modern gaming zone",
    desc: "Hit the classics in our arcade zone — racing simulators, shooting galleries, air hockey, claw machines, and ticket redemption games. Old-school fun meets modern cabinets for every type of gamer.",
    features: ["Classic arcade cabinets", "Racing simulators", "Ticket redemption games", "Air hockey & more", "Gaming tournaments"],
    icon: Gamepad2,
    image: "https://images.unsplash.com/photo-1760737188235-797715d85a64?w=800&h=500&fit=crop",
  },
];

export default function ComingSoon() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  const navLinks = [
    { label: "Home", href: "#/" },
    { label: "Activities", href: "#activities" },
    { label: "Get the App", href: "#register" },
    { label: "Contact", href: "#contact-cs" },
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: C.white, color: C.lightText, minHeight: "100vh" }}>

      {/* ── NAVBAR (dark) ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(12,18,21,0.95)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${C.border}` }}>
        <div className="wc-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 92 }}>
          <a href="#/" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src="/wildcatz-logo.png" alt="Wildcatz Indoor Sports" style={{ height: 50 }} className="wc-nav-logo" />
            <div style={{ width: 1, height: 56, background: "rgba(255,255,255,0.15)" }} />
            <a href="https://book.ninjaparc.com.au/location/townsville" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center" }}>
              <img src="/ninjaparc-logo.svg" alt="Ninja Parc" style={{ height: 75 }} className="wc-nav-logo-np" />
            </a>
          </a>

          <div className="wc-desktop-nav" style={{ display: "flex", alignItems: "center", gap: 32 }}>
            {navLinks.map(l => (
              <a key={l.label} href={l.href} style={{ fontSize: "0.85rem", fontWeight: 500, color: C.textMuted, transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = C.white} onMouseLeave={e => e.target.style.color = C.textMuted}>
                {l.label}
              </a>
            ))}
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
              style={{ background: C.teal, color: C.white, padding: "10px 24px", borderRadius: 10, fontSize: "0.85rem", fontWeight: 700 }}>
              Register Now
            </a>
          </div>

          <button className="wc-mobile-burger" onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: "none", background: "none", border: "none", color: C.white, cursor: "pointer", padding: 4 }}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {menuOpen && (
          <div className="wc-mobile-menu" style={{ background: C.card, borderTop: `1px solid ${C.border}`, padding: "16px 20px" }}>
            {navLinks.map(l => (
              <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}
                style={{ display: "block", padding: "12px 0", fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}`, fontSize: "0.95rem" }}>
                {l.label}
              </a>
            ))}
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: C.teal, color: C.white, padding: "12px", borderRadius: 10, fontWeight: 700, marginTop: 12 }}>
              Register Now
            </a>
          </div>
        )}
      </nav>

      {/* ── HERO (dark) ── */}
      <section style={{ position: "relative", overflow: "hidden", padding: "80px 0 60px", background: `linear-gradient(180deg, ${C.black} 0%, #0f1a1d 100%)` }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 30%, rgba(26,138,125,0.12) 0%, transparent 60%)" }} />
        <div style={{ position: "absolute", top: -100, left: -100, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(196,30,30,0.06), transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: -100, right: -100, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(26,138,125,0.06), transparent 70%)" }} />

        <div className="wc-container" style={{ position: "relative", textAlign: "center" }}>
          <a href="#/" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.textMuted, fontSize: "0.85rem", fontWeight: 500, marginBottom: 32 }}>
            <ArrowLeft size={16} /> Back to Wildcatz
          </a>

          <div style={{ display: "block" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(196,30,30,0.1)", border: "1px solid rgba(196,30,30,0.25)", borderRadius: 20, padding: "6px 16px", marginBottom: 28 }}>
              <Sparkles size={14} color={C.red} />
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: C.redLight }}>Coming Late 2026</span>
            </div>
          </div>

          <h1 className="wc-hero-title" style={{ fontSize: "clamp(2.2rem, 5vw, 3.5rem)", fontWeight: 900, lineHeight: 1.08, letterSpacing: "-1.5px", color: C.white, marginBottom: 24, maxWidth: 700, margin: "0 auto 24px" }}>
            More Than Sport.<br />
            <span style={{ color: C.teal }}>A Whole New Wildcatz.</span>
          </h1>
          <p style={{ fontSize: "1.1rem", lineHeight: 1.7, color: C.textMuted, maxWidth: 600, margin: "0 auto 20px" }}>
            Wildcatz is expanding with five exciting new activities — bowling, golf simulators, AR sports, virtual reality and arcade. All under one roof in Townsville.
          </p>
          <p style={{ fontSize: "0.88rem", lineHeight: 1.6, color: C.textDim, maxWidth: 500, margin: "0 auto 40px" }}>
            Download our app and create an account to stay up to date with all the news!
          </p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#activities" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.teal, color: C.white, padding: "14px 32px", borderRadius: 12, fontWeight: 700, fontSize: "0.95rem" }}>
              See What's Coming <ChevronRight size={18} />
            </a>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.border}`, color: C.white, padding: "14px 32px", borderRadius: 12, fontWeight: 600, fontSize: "0.95rem" }}>
              Download the App <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ── ACTIVITY CARDS (light bg, alternating) ── */}
      <section id="activities" style={{ padding: "80px 0", background: C.offWhite }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.teal, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>New Activities</div>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.lightText, letterSpacing: "-0.5px" }}>Five New Ways to Play</h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {COMING_SOON.map((item, i) => {
              const isExpanded = expandedCard === i;
              return (
                <div key={i} style={{ background: C.white, borderRadius: 20, border: `1px solid ${C.borderLight}`, overflow: "hidden", transition: "box-shadow 0.3s", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = `0 8px 30px ${item.color}18`}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"}>

                  <div className="cs-card-layout" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                    {/* Image side — if `image` starts with "gradient:" we
                        render a CSS gradient + big emoji hero instead of a
                        stock photo. Useful when we don't have / can't trust
                        a real photo for the activity yet. */}
                    <div style={{ position: "relative", minHeight: 300, overflow: "hidden", order: i % 2 === 0 ? 0 : 1 }}>
                      {item.image && item.image.startsWith("gradient:") ? (
                        <div style={{
                          position:'absolute', inset:0,
                          background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 50%, #0a1a1d 100%)`,
                          display:'flex', alignItems:'center', justifyContent:'center',
                        }}>
                          <div style={{ fontSize: 'clamp(8rem, 18vw, 14rem)', filter:'drop-shadow(0 8px 24px rgba(0,0,0,0.4))', opacity: 0.95 }}>{item.emoji}</div>
                          {/* Subtle pattern overlay for texture */}
                          <div style={{ position:'absolute', inset:0, background:'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.08), transparent 50%)' }} />
                        </div>
                      ) : (
                        <img src={item.image} alt={item.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }} />
                      )}
                      {!item.image?.startsWith("gradient:") && (
                        <div style={{ position: "absolute", inset: 0, background: i % 2 === 0
                          ? "linear-gradient(to right, transparent 40%, rgba(255,255,255,0.95) 100%)"
                          : "linear-gradient(to left, transparent 40%, rgba(255,255,255,0.95) 100%)" }} />
                      )}
                      <div style={{ position: "absolute", top: 20, left: 20, display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ background: C.white, border: `1px solid ${C.borderLight}`, borderRadius: 12, padding: "6px 14px", fontSize: "0.72rem", fontWeight: 700, color: item.color, display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                          <Timer size={12} /> Coming Soon
                        </div>
                      </div>
                      {/* Bottom-left emoji only when there's a photo (gradient already has the big emoji centred) */}
                      {!item.image?.startsWith("gradient:") && (
                        <div style={{ position: "absolute", bottom: 24, left: 24, fontSize: "3.5rem" }}>{item.emoji}</div>
                      )}
                    </div>

                    {/* Content side */}
                    <div style={{ padding: "36px 32px", display: "flex", flexDirection: "column", justifyContent: "center", order: i % 2 === 0 ? 1 : 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <item.icon size={20} color={item.color} />
                        <h3 style={{ fontSize: "1.5rem", fontWeight: 900, color: C.lightText }}>{item.name}</h3>
                      </div>
                      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: item.color, marginBottom: 16 }}>{item.tagline}</div>
                      <p style={{ fontSize: "0.92rem", color: C.lightMuted, lineHeight: 1.7, marginBottom: 20 }}>{item.desc}</p>

                      {/* Features */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
                        {item.features.slice(0, isExpanded ? item.features.length : 3).map((f, fi) => (
                          <span key={fi} style={{ background: C.offWhite, border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: "6px 14px", fontSize: "0.78rem", color: C.lightText }}>
                            {f}
                          </span>
                        ))}
                        {!isExpanded && item.features.length > 3 && (
                          <button onClick={() => setExpandedCard(i)}
                            style={{ background: "none", border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: "6px 14px", fontSize: "0.78rem", color: C.lightMuted, cursor: "pointer" }}>
                            +{item.features.length - 3} more
                          </button>
                        )}
                      </div>

                      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 8, background: item.color + "12", border: `1px solid ${item.color}28`, color: item.color, padding: "12px 24px", borderRadius: 10, fontWeight: 600, fontSize: "0.88rem", alignSelf: "flex-start" }}>
                        Download the App <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TIMELINE (dark bg) ── */}
      <section style={{ padding: "80px 0", background: C.black }}>
        <div className="wc-container">
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.red, letterSpacing: 3, textTransform: "uppercase", marginBottom: 12 }}>The Plan</div>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px" }}>What's Happening & When</h2>
          </div>

          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            {[
              { phase: "Phase 1", label: "VR, Arcade & Golf Simulators", time: "Jan 2027", color: C.teal, emoji: "🥽 🕹️ ⛳" },
              { phase: "Phase 2", label: "Bowling & AR Sports", time: "Mar 2027", color: C.purple, emoji: "🎳 🎯" },
            ].map((phase, i) => (
              <div key={i} style={{ display: "flex", gap: 20 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 40, flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: phase.color, border: `3px solid ${C.black}`, flexShrink: 0, zIndex: 1 }} />
                  {i < 1 && <div style={{ width: 2, flex: 1, background: `linear-gradient(to bottom, ${phase.color}66, ${C.border})`, minHeight: 40 }} />}
                </div>
                <div style={{ paddingBottom: 32 }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, color: phase.color, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{phase.phase} · {phase.time}</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 800, color: C.white, marginBottom: 4 }}>{phase.label}</div>
                  <div style={{ fontSize: "1.2rem" }}>{phase.emoji}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <p style={{ fontSize: "1rem", fontWeight: 700, color: C.teal, marginBottom: 8 }}>
              More to be announced!
            </p>
            <p style={{ fontSize: "0.85rem", color: C.textMuted, fontStyle: "italic" }}>
              Dates are approximate and subject to change. Download our app to stay updated.
            </p>
          </div>
        </div>
      </section>

      {/* ── DOWNLOAD APP CTA (teal bg) ── */}
      <section id="register" style={{ padding: "80px 0", background: C.teal }}>
        <div className="wc-container" style={{ textAlign: "center" }}>
          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, color: C.white, letterSpacing: "-0.5px", marginBottom: 16 }}>
              Be First to Know
            </h2>
            <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.85)", lineHeight: 1.7, marginBottom: 32 }}>
              Download our app and create an account to stay up to date with all the news! Be the first to know when each new activity opens for bookings.
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" style={{ height: 52 }} />
              </a>
              {GOOGLE_PLAY_URL && (
                // 2026-06-14: Google's PNG badge has ~22% internal padding so
                // at height: 52 it looks noticeably smaller than Apple's
                // tight-cropped SVG. Scale img up by 1.28x and clip the
                // overflow inside a 52px wrapper so the visible artwork
                // matches Apple's badge optically.
                <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', height: 52, overflow: 'hidden', lineHeight: 0 }}>
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    style={{ height: 67, display: 'block', marginTop: -7.5, marginBottom: -7.5 }}
                  />
                </a>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginTop: 8, flexWrap: "wrap" }}>
              {[
                { icon: Zap, text: "Push notifications" },
                { icon: Star, text: "Live scores & news" },
                { icon: Users, text: "Early access" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", color: "rgba(255,255,255,0.8)" }}>
                  <item.icon size={14} color="rgba(255,255,255,0.9)" /> {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT (light bg) ── */}
      <section id="contact-cs" style={{ padding: "60px 0", background: C.offWhite, borderTop: `1px solid ${C.borderLight}` }}>
        <div className="wc-container">
          <div style={{ textAlign: "center" }}>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: C.lightText, marginBottom: 20 }}>Got Questions?</h3>
            <div className="cs-contact-row" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
              <a href="tel:0747231414" style={{ display: "flex", alignItems: "center", gap: 8, color: C.lightText, fontSize: "0.9rem" }}>
                <Phone size={16} color={C.teal} /> (07) 4723 1414
              </a>
              <a href="mailto:Info@wildcatzindoorsports.com.au" style={{ display: "flex", alignItems: "center", gap: 8, color: C.lightText, fontSize: "0.9rem" }}>
                <Mail size={16} color={C.teal} /> Info@wildcatzindoorsports.com.au
              </a>
              <span style={{ display: "flex", alignItems: "center", gap: 8, color: C.lightText, fontSize: "0.9rem" }}>
                <MapPin size={16} color={C.teal} /> Wildcatz Indoor Sports, Townsville
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER (dark with teal bar) ── */}
      <footer style={{ borderTop: `3px solid ${C.teal}`, padding: "40px 0", background: C.darkBg }}>
        <div className="wc-container">
          <div className="wc-footer-grid" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/wildcatz-logo.png" alt="Wildcatz" style={{ height: 32, opacity: 0.8 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 24, fontSize: "0.78rem", color: C.textMuted }}>
              <a href="#/" style={{ color: C.teal, fontWeight: 600 }}>← Back to Home</a>
              <span>Powered by <a href="https://www.wisasports.com" target="_blank" rel="noopener noreferrer" style={{ color: C.teal, fontWeight: 600 }}>WISA</a></span>
            </div>
            <div style={{ fontSize: "0.75rem", color: C.textDim }}>
              © {new Date().getFullYear()} Wildcatz Indoor Sports. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* ── STYLES ── */}
      <style>{`
        * { box-sizing: border-box; }
        a { text-decoration: none; }

        .wc-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        @media (max-width: 768px) {
          .wc-desktop-nav { display: none !important; }
          .wc-mobile-burger { display: block !important; }
          .cs-card-layout { grid-template-columns: 1fr !important; }
          .cs-card-layout > div:first-child { order: 0 !important; min-height: 200px !important; }
          .cs-card-layout > div:last-child { order: 1 !important; }
          .cs-contact-row { flex-direction: column !important; gap: 16px !important; }
          .wc-footer-grid { flex-direction: column !important; text-align: center !important; }
          .wc-nav-logo { height: 36px !important; }
        }
        @media (min-width: 769px) {
          .wc-mobile-burger { display: none !important; }
          .wc-mobile-menu { display: none !important; }
        }
      `}</style>
    </div>
  );
}
